import {
  CustomerDomain,
  CustomerDomainPayload,
  useAddCustomerDomainMutation,
  useGetCustomerConfigQuery,
} from "@/redux/reducer/customers.api";
import { useGetPagesQuery } from "@/redux/reducer/pages.api";
import { getUserTenantId, getUserSupportStatus } from "@talentech/bff-login";
import {
  PageTitle,
  ButtonColors,
  Grid,
  Box,
  ModalForm,
  TalentechFormFieldTypes,
  Button,
  Typography,
  Row,
  BootstrapSizes,
  TalentechColors,
  SplashScreen,
  TalentechSvgImages,
} from "@talentech/components";
import { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import DomainCard from "@/components/DomainCard/DomainCard";
import { rejectionMessage } from "@/components/DomainCard/domainAddress";
import { NEW_JOB_ID, jobPath } from "@/pages/Jobs/jobRoutes";

const validationSchema = yup.object().shape({
  domain: yup.object().shape({
    domain_name: yup.string().required("This field is required"),
    customer_alias: yup
      .string()
      .matches(
        /^\S*$/,
        "This field must not contain spaces or white characters"
      )
      .matches(/^[a-z0-9]*$/, "This field must contain only lowercase letters and numbers"),
    domain_url: yup
      .string()
      .url("This field must be a valid URL")
      .required("This field is required"),
    public_url: yup
      .string()
      .test(
        "is-http-url",
        "This field must be a URL starting with http:// or https://",
        (value) => !value || /^https?:\/\/\S+$/.test(value)
      ),
  }),
});

const Branding = () => {
  const tenantId = useSelector(getUserTenantId);
  const { data } = useGetCustomerConfigQuery({ tenantId });
  const [isOpen, setIsOpen] = useState(false);
  const { pages, ads } = useGetPagesQuery(
    {},
    {
      selectFromResult: (res) => ({
        pages: res.data?.page_templates || [],
        ads: res.data?.job_templates || [],
      }),
    }
  );

  const isSupport = useSelector(getUserSupportStatus);
  const navigate = useNavigate();

  const sourceData = data;
  const templates = sourceData?.domains || [];

  const getPageDetails = (pageId: string) =>
    pages.find((page) => page.id === pageId);

  const [addCustomerDomain] = useAddCustomerDomainMutation();
  const onAddNewDomain = (details: {
    domain: {
      domain_name: string;
      domain_url: string;
      customer_alias?: string;
      public_url?: string;
      is_self_hosted: boolean;
    };
  }) => {
    const hostingFields = details.domain.is_self_hosted
      ? {
          domain_type: "custom",
          domain_url: details.domain.domain_url,
          // Outward link base for reverse-proxied custom domains; omit when empty
          ...(details.domain.public_url
            ? { public_url: details.domain.public_url }
            : {}),
        }
      : {
          domain_type: "hosted",
          customer_alias: details.domain.customer_alias,
        };

    // @ts-ignore
    const preparedData: CustomerDomainPayload = {
      tenantId,
      domain_name: details.domain.domain_name,
      is_primary: true,
      ...hostingFields,
    };

    const toastId = toast.loading("Adding new domain...");
    addCustomerDomain(preparedData)
      .unwrap()
      .then(() => {
        toast.update(toastId, { render: "Domain added successfully", type: "success", isLoading: false, autoClose: null });
        setIsOpen(false);
      })
      .catch((err) => {
        toast.update(toastId, {
          render: rejectionMessage(err, "Failed to add domain"),
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      });
  };

  const [formShape, setFormShape] = useState<any>({
    self_hosted: false,
    customer_alias: "",
  });

  const domainFields = [
    {
      type: TalentechFormFieldTypes.TEXT,
      label: "Name",
      field: "domain_name",
      size: {
        xs: 12,
      },
    },
    formShape.self_hosted
      ? null
      : {
          type: TalentechFormFieldTypes.TEXT,
          label: "Alias",
          field: "customer_alias",
          size: {
            xs: 12,
          },
          callback: (val: any) => {
            setFormShape({ ...formShape, customer_alias: val });
          },
        },
    {
      type: TalentechFormFieldTypes.TEXT,
      label: "URL",
      field: "domain_url",
      size: {
        xs: 12,
      },
      initialValue: formShape.self_hosted
        ? ""
        : "https://jobs.talentech.com/" + formShape.customer_alias,
      readOnly: !formShape.self_hosted,
    },

    formShape.self_hosted
      ? {
          type: TalentechFormFieldTypes.TEXT,
          label: "Public link URL (optional)",
          field: "public_url",
          size: {
            xs: 12,
          },
          options: {
            placeholder: "https://www.customer.com/careers",
          },
          additionalContent: (
            <Typography variant="p" className="small text-muted mb-0">
              Only for sites reverse-proxied under the customer&apos;s own
              website. Job links in feeds and on published pages will use this
              address instead of the URL above; the URL above must stay the
              address our platform actually serves (routing).
            </Typography>
          ),
        }
      : null,
    {
      type: TalentechFormFieldTypes.SWITCH,
      label: "Self-hosted",
      field: "is_self_hosted",
      size: {
        xs: 12,
      },
      callback: (val: any) => {
        setFormShape({ ...formShape, self_hosted: val });
      },
    },
  ].filter((field) => !!field);

  const isMisconfigured = (domain: CustomerDomain) => {
    return (
      !!domain.joblist_page?.joblist_page_id &&
      !getPageDetails(domain.joblist_page?.joblist_page_id)
    );
  };

  const getDomainStatusConfig = (domain: CustomerDomain) => {
    if (isMisconfigured(domain)) {
      return {
        label: "MISCONFIGURED",
        color: TalentechColors.DANGER,
      };
    }

    if (getPageDetails(domain.joblist_page?.joblist_page_id)?.published) {
      return {
        label: "Published",
        color: TalentechColors.SUCCESS,
      };
    }

    if (domain.joblist_page) {
      return {
        label: "DRAFT",
        color: TalentechColors.INFO,
      };
    }

    return {
      label: "not configured",
      color: TalentechColors.WARNING,
    };
  };

  const selectablePagesAndAds = useMemo(() => {
    const assignedPages: string[] = [];

    templates.forEach((template) => {
      if (template.joblist_page?.joblist_page_id) {
        assignedPages.push(template.joblist_page.joblist_page_id);
      }
    });

    const selectablePagesAndAds = {
      pages: pages.filter((page) => !assignedPages.includes(page.id)),
      ads,
    };

    return selectablePagesAndAds;
  }, [templates, pages, ads]);

  return (
    <>
      <PageTitle title={"Manage your career sites"} />

      <Grid container className="mt-4">
        
        <Row>
          {isSupport && (
            <Grid md={6} className="mb-4 d-flex">
              <div className="card flex-fill">
                <div className="card-body d-flex flex-column">
                  <Typography variant="h2" className="h5 card-title mb-3">
                    Build a Career Site
                  </Typography>
                  <Typography className="mb-4">
                    Create a standout career site to attract top talent with
                    targeted job ads. Customize the design to match your brand
                    and give candidates a smooth, engaging experience.
                  </Typography>
                  <div className="mt-auto">
                    <Button
                      color={ButtonColors.SUCCESS}
                      label="Start building your new site"
                      onClick={() => setIsOpen(true)}
                      size={BootstrapSizes.LARGE}
                      icon={<i className="fa-sharp fa-light fa-rocket me-2" />}
                    />
                  </div>
                </div>
              </div>
            </Grid>
          )}
          <Grid md={6} className="mb-4 d-flex">
            <div className="card flex-fill">
              <div className="card-body d-flex flex-column">
                <Typography variant="h2" className="h5 card-title mb-3">
                  Post a job directly
                </Typography>
                <Typography className="mb-4">
                  Write the job here and publish it to your career sites. Fill
                  in the details once and your job ad template lays them out
                  for you.
                </Typography>
                <div className="mt-auto">
                  <Button
                    color={ButtonColors.SUCCESS}
                    label="Create a job"
                    onClick={() =>
                      navigate(jobPath(NEW_JOB_ID), { viewTransition: true })
                    }
                    size={BootstrapSizes.LARGE}
                    icon={<i className="fa-sharp fa-light fa-plus me-2" />}
                  />
                </div>
              </div>
            </div>
          </Grid>
        </Row>
        <Row>
          {!sourceData?.domains.length ? (
            <Grid>
              <Box paper>
                <SplashScreen
                  size={"sm"}
                  subtitle="Let's get started by adding your first brand"
                  isFullPage={false}
                  customComponent={
                    <Button
                      color={ButtonColors.PRIMARY}
                      label="Add New brand"
                      icon={<i className="fa-sharp fa-light fa-plus me-2" />}
                      onClick={() => setIsOpen(true)}
                    />
                  }
                  image={TalentechSvgImages.BUSINESS_MAGICIAN_HAT_RABBIT}
                  productName="Career Sites"
                  title="There are no brands yet"
                />
              </Box>
            </Grid>
          ) : (
            <>
              <div className="row row-cols-1 row-cols-md-2 row-cols-lg-2 row-cols-xl-3 g-4">
                {templates.map((template) => (
                  <DomainCard
                    key={template.domain_id}
                    domain={template}
                    statusConfig={getDomainStatusConfig(template)}
                    pages={pages}
                    ads={ads}
                    selectablePagesAndAds={selectablePagesAndAds}
                  />
                ))}
              </div>
            </>
          )}
        </Row>
      </Grid>
      <ModalForm
        modal={{
          showModal: isOpen,
          title: "Add new domain",
          toggleModal: () => setIsOpen(!isOpen),
        }}
        form={{
          validationSchema,
          handleSubmit: onAddNewDomain,
          formFields: {
            fields: domainFields as any,
            id: "domain",
            section: "",
          },
        }}
      />
    </>
  );
};
export default Branding;
